import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Row,
  Col
} from 'reactstrap';
import { Link } from 'react-router-dom';
import {
  fetchUsers,
  activateUser,
  validateUniqueEmail,
  deactivateUser,
} from '../../actions/profile/apiRequests';
import { notifyError } from '../../utils/notifier';
import messages from '../../constants/messages';
import withRoleRestrictions from '../shared/withRoleRestrictions';
import withEulaRestrictions from '../license/withEulaRestrictions';
import Button from '../shared/Button';
import Page from '../shared/Page';
import Loader from '../shared/Loader';
import Pagination from '../shared/Pagination';
import ActionModal from '../shared/ActivationModal';
import UsersListTable from './UsersListTable';
import UsersListFilter from './UsersListFilter';
import { ADMINS } from '../../constants/userRoles';
import { getCompanyMembersForAdmin } from '../../actions/profile/apiRequests';
import { getBreadCrumbs } from '../../utils/breadCrumbs';
import {
  DASHBOARD,
  PAGE_PATHS
} from '../../constants/pathsRoots';


const PAGE_SIZE = 20;

export class UsersListPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      users: [],
      page: 1,
      size: 1,
      totalCount: null,
      pageCount: 1,
      query: '',
      loading: true,
      showUserStatusModal: false,
      action: {}
    };
    this.onChangePage = this.onChangePage.bind(this);
    this.onChangeQuery = this.onChangeQuery.bind(this);
    this.fetchResults = this.fetchResults.bind(this);
    this.toggleUserStatusModal = this.toggleUserStatusModal.bind(this);
    this.onStatusChangeSuccess = this.onStatusChangeSuccess.bind(this);
    this.onStatusChangeError = this.onStatusChangeError.bind(this);
  }

  static get propTypes() {
    return {
      user: PropTypes.object,
      location: PropTypes.object,
    }
  }

  get companyId() {
    const { location = {} } = this.props;
    return location.state && location.state.companyId;
  }

  get actions() {
    return [
      <Link to='/user' key='add'>
        <Button kind='primary' className='mr-1'>Add User</Button>
      </Link>
    ];
  }

  componentDidMount() {
    const { page } = this.state;
    const currentUser = this.props.user;

    if (this.companyId && currentUser.isSpruceAdmin) {
      this.fetchResultsByCompanyId(page);
    } else {
      this.fetchResults(page);
    }
  }

  fetchResultsByCompanyId(page) {
    this.setState({ loading: true });
    return getCompanyMembersForAdmin(page, PAGE_SIZE, this.companyId)
      .then(({
        data,
        page,
        size,
        pageCount,
        totalCount
      }) =>
        this.setState({
          users: data,
          page,
          size,
          pageCount,
          totalCount,
          loading: false
        })
      )
      .catch(() => {
        this.setState({ loading: false });
        notifyError(messages.request.usersListError);
      });
  }

  fetchResults(page) {
    const { query } = this.state;
    this.setState({ loading: true });

    return fetchUsers(page, PAGE_SIZE, query)
      .then(({
        data,
        page,
        size,
        pageCount,
        totalCount
      }) => this.setState({
        users: data,
        page,
        size,
        pageCount,
        totalCount,
        loading: false
      }))
      .catch(() => {
        this.setState({ loading: false });
        notifyError(messages.request.usersListError);
      });
  }

  onChangePage(page) {
    window.scrollTo(0, 0);
    this.fetchResults(page)
      .then(this.setState({ page }));
  }

  onChangeQuery(event) {
    this.setState({
      query: event.target.value
    });
  }

  toggleUserStatusModal(userId, email, userEnabled) {
    this.setState(({ showUserStatusModal }) => ({
      showUserStatusModal: !showUserStatusModal,
      action: {
        userId,
        email,
        userEnabled
      }
    }));
  }

  onStatusChangeSuccess() {
    this.setState((prevState) => ({
      users: prevState.users.map((user) => {
        if (user.id === prevState.action.userId) {
          user.enabled = !user.enabled;
        }
        return user;
      })
    }));
    this.toggleUserStatusModal();
  }

  onStatusChangeError(error) {
    let message = messages.common.error;
    if (error.status === 400) {
      message = messages.request.userActivationError;
    }
    this.toggleUserStatusModal();
    notifyError(message);
  }

  activateUser(email, id) {
    return validateUniqueEmail(email)
      .then(() => activateUser(id))
  }

  render() {
    const {
      loading,
      users,
      page,
      size,
      totalCount,
      pageCount,
      query,
      showUserStatusModal,
      action
    } = this.state;
    const { pathname } = this.props.location;
    const hasResults = users && users.length;
    const currentUser = this.props.user;
    const itemsFrom = page * size  - size + 1;
    let itemTo = page * size;
    if (totalCount - page * size < 0) {
      itemTo = totalCount;
    }

    return (
      <Page
        header='Manage Users'
        actions={this.actions}
        breadCrumbs={getBreadCrumbs('Manage Users', pathname, [PAGE_PATHS[DASHBOARD]])}
      >
        <Row>
          <Col>
            <UsersListFilter
              query={query}
              placeholder={`Search by ${currentUser.isSpruceAdmin ? 'Company name,': '' } User Name, Email and User Type`}
              onChangeQuery={this.onChangeQuery}
              onSubmit={this.fetchResults}
              {...this.props}
            />
          </Col>
        </Row>
        <If condition={totalCount>0}>
          <Row>
            <div className='ml-3 col-md-3'>
              <p>{`${itemsFrom} — ${itemTo} out of ${totalCount} results`}</p>
            </div>
          </Row>
        </If>
        <Choose>
          <When condition={!loading && (!hasResults)}>
            <div className='text-center my-5 py-5'>
              No users found
            </div>
          </When>
          <Otherwise>
            <Row className='mb-2'>
              <Col>
                <If condition={hasResults}>
                  <UsersListTable
                    users={users}
                    onChangeStatus={this.toggleUserStatusModal}
                  />
                </If>
              </Col>
            </Row>
            <Row>
              <Col>
                <Choose>
                  <When condition={loading}>
                      <Loader />
                  </When>
                  <Otherwise>
                    <If condition={pageCount > 1}>
                      <Pagination
                        activePage={page}
                        itemsCountPerPage={PAGE_SIZE}
                        totalItemsCount={totalCount}
                        onChange={this.onChangePage}
                      />
                    </If>
                  </Otherwise>
                </Choose>
              </Col>
            </Row>
          </Otherwise>
        </Choose>
        <ActionModal
          message={`Are you sure you want to ${action.userEnabled ? 'deactivate' : 'activate'} this user?`}
          isOpen={showUserStatusModal}
          action={() => action.userEnabled ? deactivateUser(action.userId) : this.activateUser(action.email, action.userId)}
          onCancel={this.toggleUserStatusModal}
          onSuccess={this.onStatusChangeSuccess}
          onError={this.onStatusChangeError}
        />
      </Page>
    );
  }
}

const mapStateToProps = ({ profile }) => ({
  user: profile
});

export default connect(mapStateToProps)(
  withEulaRestrictions(withRoleRestrictions(UsersListPage, ADMINS))
);



// WEBPACK FOOTER //
// src/components/usersList/UsersListPage.jsx